import { useState, useMemo } from 'react';
import { inferTableStructure } from '../../utils/tableInference';
import PreviewTable from './PreviewTable';

function ScatterMappingPanel({ columns, rows, initialMapping, onConfirm, onBack, ui }) {
  const numericColumns = columns.filter((c) => c.type === 'number');
  const stringColumns = columns.filter((c) => c.type === 'string');
  const inferred = useMemo(() => inferTableStructure(rows, columns, 'scatter-plot'), [rows, columns]);

  const [xCol, setXCol] = useState(initialMapping.x || numericColumns[0]?.name || '');
  const [yCol, setYCol] = useState(initialMapping.y || numericColumns[1]?.name || '');
  const [categoryCol, setCategoryCol] = useState(initialMapping.category || '');
  const [sizeCol, setSizeCol] = useState(initialMapping.size || '');

  const canConfirm = Boolean(xCol) && Boolean(yCol) && xCol !== yCol;

  const handleConfirm = () => {
    onConfirm({
      x: xCol,
      y: yCol,
      ...(categoryCol ? { category: categoryCol } : {}),
      ...(sizeCol ? { size: sizeCol } : {}),
    });
  };

  return (
    <div>
      {inferred?.notes && (
        <div className="border p-2.5 mb-3 text-xs leading-relaxed" style={{ color: ui.textSecondary, borderColor: ui.borderSecondary, backgroundColor: ui.bgSecondary }}>
          <span className="font-medium" style={{ color: ui.text }}>Auto-detected: </span>{inferred.notes}
        </div>
      )}
      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <label className="text-xs" style={{ color: ui.text }}>
          <div className="mb-1 font-medium">X coordinate</div>
          <select value={xCol} onChange={(e) => { const nextX = e.target.value; if (nextX === yCol) setYCol(xCol); setXCol(nextX); }} className="w-full text-xs border py-1 px-2" style={{ borderColor: ui.borderSecondary }}>
            {numericColumns.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </label>
        <label className="text-xs" style={{ color: ui.text }}>
          <div className="mb-1 font-medium">Y coordinate</div>
          <select value={yCol} onChange={(e) => { const nextY = e.target.value; if (nextY === xCol) setXCol(yCol); setYCol(nextY); }} className="w-full text-xs border py-1 px-2" style={{ borderColor: ui.borderSecondary }}>
            {numericColumns.filter((c) => c.name !== xCol).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </label>
      </div>
      <div className="grid gap-3 mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <label className="text-xs" style={{ color: ui.text }}>
          <div className="mb-1 font-medium">Color by (optional)</div>
          <select value={categoryCol} onChange={(e) => setCategoryCol(e.target.value)} className="w-full text-xs border py-1 px-2" style={{ borderColor: ui.borderSecondary }}>
            <option value="">None</option>
            {stringColumns.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </label>
        <label className="text-xs" style={{ color: ui.text }}>
          <div className="mb-1 font-medium">Size by (optional)</div>
          <select value={sizeCol} onChange={(e) => setSizeCol(e.target.value)} className="w-full text-xs border py-1 px-2" style={{ borderColor: ui.borderSecondary }}>
            <option value="">None</option>
            {numericColumns.filter((c) => c.name !== xCol && c.name !== yCol).map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </label>
      </div>
      <PreviewTable columns={columns} rows={rows} ui={ui} />
      <div className="flex gap-2">
        <button onClick={onBack} className="flex-1 text-xs border py-2 ui-hover ui-border" style={{ borderColor: ui.border, color: ui.text }}>&larr; Back</button>
        <button onClick={handleConfirm} disabled={!canConfirm} className={`flex-1 text-xs border py-2 font-medium ui-border ${!canConfirm ? 'opacity-40 cursor-not-allowed' : 'ui-hover'}`} style={{ borderColor: ui.border, color: ui.text }}>
          Confirm &rarr;
        </button>
      </div>
    </div>
  );
}

export default ScatterMappingPanel;
